import FluidImage from "@/components/images/fluid-image";
import { FLUID_CDN_URL } from "@/lib/constants";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import React, { ChangeEvent, FocusEvent, useState } from "react";
import {
  Control,
  FieldPath,
  FieldValues,
  useController,
} from "react-hook-form";

interface DropdownSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "name"> {
  control: Control<TFieldValues>;
  label: string;
  options: { id: string | number; name: string }[];
  id: string;
  name: TName;
  containerStyles?: string;
  onInputChange?: (option: { id: string | number; name: string }) => void;
  inputStyles?: string;
  error?: { message: string } | undefined;
  onChange?: () => void;
  placeholder?: string;
  placeholderIcon?: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
  notFoundMessage?: string;
  disableCapitalize?: boolean;
}

export default function DropdownSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  label,
  options,
  id,
  name,
  containerStyles,
  onInputChange,
  inputStyles,
  error: externalError,
  onChange,
  placeholder,
  placeholderIcon,
  notFoundMessage,
  disableCapitalize = false,
}: DropdownSelectProps<TFieldValues, TName>) {
  const { t } = useTranslation();
  const {
    field,
    fieldState: { error: internalError },
  } = useController({
    name,
    control,
  });

  const error = externalError || internalError;

  const [searchValue, setSearchValue] = useState("");
  const [labelPosition, setLabelPosition] = useState({
    top: "1/2",
    textSize: "text-sm",
  });
  const [isFocused, setIsFocused] = useState(false);

  const filteredOptions = searchValue
    ? options.filter((option) =>
        option.name.toLowerCase().includes(searchValue.toLowerCase()),
      )
    : options;

  const handleSelect = (
    option: { id: string | number; name: string } | null,
  ) => {
    if (option) {
      field.onChange(option.name);
      onInputChange?.(option);
      setSearchValue("");

      onChange?.();
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    field.onChange(value);
    setSearchValue(value);

    if (value) {
      onChange?.();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    setLabelPosition({ top: "1/4", textSize: "text-xs" });
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    setLabelPosition({ top: "1/2", textSize: "text-sm" });
    if (!filteredOptions?.some((option) => option.name === e.target.value)) {
      field.onChange("");
    }
    if (field.value) {
      setLabelPosition({ top: "1/4", textSize: "text-xs" });
    }
  };

  return (
    <div className={cn("relative w-full flex-1", containerStyles)}>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <Combobox
        as="div"
        className="relative"
        value={field.value || ""}
        // Combobox expects the same type as value for onChange, but we handle the conversion internally
        onChange={
          handleSelect as (
            value: string | { id: string | number; name: string } | null,
          ) => void
        }
      >
        <ComboboxInput
          id={id}
          type="text"
          value={typeof field.value === "string" ? field.value : ""}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleInputChange}
          name={name}
          {...(placeholder !== undefined && { placeholder })}
          className={cn(
            "inputText peer ring-b-0 h-input relative z-10 w-full border-0 pt-5 text-base ring-1 focus:z-20 focus:ring-2 focus:ring-inset sm:text-sm",
            !disableCapitalize && "text-capitalize capitalize",
            error
              ? "shadow-error z-20 ring-red-500"
              : "ring-gray-300 focus:ring-black",
            inputStyles,
          )}
        />
        {isFocused && filteredOptions.length > 0 ? (
          <ComboboxOptions className="absolute z-40 mt-1 max-h-60 w-full overflow-auto rounded-b-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm">
            {filteredOptions.map((option) => (
              <ComboboxOption
                key={option.id}
                value={option}
                className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-[focus]:bg-gray-100 data-[focus]:outline-hidden"
              >
                <span className="block whitespace-pre-line group-data-[selected]:font-semibold">
                  {option.name}
                </span>
                <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-gray-900 group-data-[selected]:flex"></span>
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        ) : isFocused && filteredOptions.length === 0 ? (
          <ComboboxOptions className="absolute z-40 mt-1 max-h-60 w-full overflow-auto rounded-b-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm">
            <ComboboxOption value={null} disabled className="p-3">
              <div className="flex items-center rounded-md bg-amber-50 p-2 text-amber-700">
                <svg
                  className="mr-2 h-4 w-4 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-xs">
                  {notFoundMessage ?? t("no_results_found")}
                </span>
              </div>
            </ComboboxOption>
          </ComboboxOptions>
        ) : null}
        <ComboboxButton className="cursor-text">
          <span
            className={cn(
              "absolute left-3 z-20 -translate-y-1/2 transform text-gray-400 transition-all peer-focus:text-gray-700",
              `top-${labelPosition.top}`,
              labelPosition.textSize,
              field.value && "top-1/4 text-xs",
            )}
            onClick={handleFocus}
          >
            {label}
          </span>
          {placeholderIcon && (
            <FluidImage
              src={placeholderIcon.src}
              alt={placeholderIcon.alt}
              width={placeholderIcon.width}
              height={placeholderIcon.height}
              className="pointer-events-none absolute bottom-[14px] left-3 z-20 pr-0 text-gray-500 opacity-60"
            />
          )}
          {!error ? (
            <FluidImage
              src={`${FLUID_CDN_URL}/images/checkout/icons/chevron-down.svg`}
              alt="chevron-down"
              width={12}
              height={12}
              className="absolute top-1/2 right-3 z-20 -translate-y-1/2 transform cursor-pointer"
              onClick={() => {
                handleFocus();
              }}
            />
          ) : (
            <FluidImage
              src={`${FLUID_CDN_URL}/images/checkout/icons/circle-exclamation.svg`}
              alt="error"
              width={14}
              height={14}
              className="red-icons absolute top-1/2 right-3 z-20 -translate-y-1/2 transform"
            />
          )}
        </ComboboxButton>
      </Combobox>
    </div>
  );
}
